import {Injectable} from '@angular/core';

/**
 * Result returned by buildHeaderFromSource() — the mapped header fields
 * that the component should apply to vm.item.header.
 */
export interface SourceHeaderMapping {
  entity_id: any;
  bill_address_id: any;
  ship_address_id: any;
  reference: any;
  notes: any;
  project_id: any;
  department_id: any;
  /** The source-link field name (e.g. 'work_order_id') */
  sourceLinkField: string;
  /** The source-link field value (the source document ID) */
  sourceLinkValue: string;
}

/**
 * A single mapped detail line ready to push onto vm.item.lines.
 */
export interface MappedLine {
  item_id: any;
  description: any;
  quantity: any;
  unit_price: number;
  discount: number;
  tax_code_id: any;
  project_id: any;
  department_id: any;

  /** Source-link field — e.g. work_order_line_id | shipment_line_id | quotation_line_id */
  [sourceLinkKey: string]: any;
}

/**
 * TransactionSourceBuilderService
 *
 * Responsible for mapping source documents (Work Orders, Shipments, Quotations)
 * into transaction header and line data. All methods are pure data transformers —
 * no HTTP calls, no notifications, no router access.
 *
 * The component is responsible for:
 *   1. Fetching the source document via tableDataService.
 *   2. Calling the appropriate builder method with the fetched data.
 *   3. Applying the returned header mapping to vm.item.header.
 *   4. Replacing vm.item.lines with the returned mapped lines.
 *   5. Triggering entity load (loadEntityHistory, loadAndProcessEntityAddresses).
 *
 * Phase 4 of the Invoice Form Refactor — see docs/Invoice Form Refactor.md
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionSourceBuilderService {

  // ---------------------------------------------------------------------------
  // Header mapping
  // ---------------------------------------------------------------------------

  /**
   * Build the common header field mapping from any source document header.
   * Works for Work Orders, Shipments, and Quotations — all share the same
   * header field names for the fields we copy.
   *
   * @param sourceHeader     The source document's header object
   * @param sourceId         The source document's ID (string)
   * @param sourceLinkField  The field name to set on the target header (e.g. 'work_order_id')
   */
  buildHeaderFromSource(sourceHeader: any, sourceId: string, sourceLinkField: string): SourceHeaderMapping {
    return {
      entity_id: sourceHeader?.entity_id ?? null,
      bill_address_id: sourceHeader?.bill_address_id ?? null,
      ship_address_id: sourceHeader?.ship_address_id ?? null,
      reference: sourceHeader?.reference ?? null,
      notes: sourceHeader?.notes ?? null,
      project_id: sourceHeader?.project_id ?? null,
      department_id: sourceHeader?.department_id ?? null,
      sourceLinkField,
      sourceLinkValue: sourceId
    };
  }

  // ---------------------------------------------------------------------------
  // Line mapping — Work Order
  // ---------------------------------------------------------------------------

  /**
   * Map billable work order lines into invoice detail lines.
   * Only lines with billable === true are included.
   *
   * Replaces: invoice-form.copyWorkOrderLinesToInvoice()
   *
   * @param workOrder  The full work order object (with a .lines array)
   * @returns          Array of mapped lines ready for vm.item.lines
   */
  mapWorkOrderLines(workOrder: any): MappedLine[] {
    const lines: MappedLine[] = [];

    if (!workOrder?.lines?.length) {
      return lines;
    }

    for (const line of workOrder.lines) {
      if (line.billable) {
        lines.push({
          item_id: line.item_id,
          description: line.description,
          quantity: Math.abs(line.quantity || 0),
          unit_price: Math.abs(line.unit_price || 0),
          discount: Math.abs(line.discount || 0),
          tax_code_id: line.tax_code_id,
          project_id: line.project_id,
          department_id: line.department_id,
          work_order_line_id: line.id
        });
      }
    }

    return lines;
  }

  // ---------------------------------------------------------------------------
  // Line mapping — Shipment
  // ---------------------------------------------------------------------------

  /**
   * Map billable shipment lines into invoice detail lines.
   * Only lines with billable === true are included.
   *
   * Replaces: invoice-form.copyShipmentLinesToInvoice()
   *
   * @param shipment  The full shipment object (with a .lines array)
   * @returns         Array of mapped lines ready for vm.item.lines
   */
  mapShipmentLines(shipment: any): MappedLine[] {
    const lines: MappedLine[] = [];

    if (!shipment?.lines?.length) {
      return lines;
    }

    for (const line of shipment.lines) {
      if (line.billable) {
        lines.push({
          item_id: line.item_id,
          description: line.description,
          quantity: Math.abs(line.quantity || 0),
          unit_price: Math.abs(line.unit_price || 0),
          discount: Math.abs(line.discount || 0),
          tax_code_id: line.tax_code_id,
          project_id: line.project_id,
          department_id: line.department_id,
          shipment_line_id: line.id
        });
      }
    }

    return lines;
  }

  // ---------------------------------------------------------------------------
  // Line mapping — Quotation
  // ---------------------------------------------------------------------------

  /**
   * Map all quotation lines into invoice detail lines.
   * All lines are included (quotations have no billable flag).
   *
   * Replaces: invoice-form.copyQuotationLinesToInvoice()
   *
   * @param quotation  The full quotation object (with a .lines array)
   * @returns          Array of mapped lines ready for vm.item.lines
   */
  mapQuotationLines(quotation: any): MappedLine[] {
    const lines: MappedLine[] = [];

    const lineArray = Array.isArray(quotation) ? quotation : (quotation?.lines ?? quotation?.detail ?? []);
    if (!lineArray.length) {
      return lines;
    }

    for (const line of lineArray) {
      lines.push({
        item_id: line.item_id,
        description: line.description,
        quantity: Math.abs(line.quantity || 0),
        unit_price: Math.abs(line.unit_price ?? line.amount ?? 0),
        discount: Math.abs(line.discount || 0),
        tax_code_id: line.tax_code_id ?? line.tax_id_1,
        project_id: line.project_id,
        department_id: line.department_id,
        quotation_line_id: line.id
      });
    }

    return lines;
  }

  // ---------------------------------------------------------------------------
  // Convenience: apply header mapping to a target header object
  // ---------------------------------------------------------------------------

  /**
   * Apply a SourceHeaderMapping onto a target header object in place.
   * The component calls this after receiving the mapping from buildHeaderFromSource().
   *
   * @param targetHeader  The vm.item.header object to mutate
   * @param mapping       The mapping returned by buildHeaderFromSource()
   */
  applyHeaderMapping(targetHeader: any, mapping: SourceHeaderMapping): void {
    targetHeader.entity_id = mapping.entity_id;
    targetHeader.bill_address_id = mapping.bill_address_id;
    targetHeader.ship_address_id = mapping.ship_address_id;
    targetHeader.reference = mapping.reference;
    targetHeader.notes = mapping.notes;
    targetHeader.project_id = mapping.project_id;
    targetHeader.department_id = mapping.department_id;
    targetHeader[mapping.sourceLinkField] = mapping.sourceLinkValue;
  }
}
